import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { getAvatar, highlightDescription } from "./SaleItem";
import {
  compact,
  every,
  forEach,
  get,
  isEmpty,
  last,
  map,
  sortBy,
} from "lodash";
import {
  deepFreeze,
  formatCurrency,
  hashCode,
  timestampToDate,
} from "../util/misc";
import COLORS from "../colors";
import { diff } from "deep-object-diff";
import ItemLocation from "./ItemLocation";

export const INVENTORY_ITEM_FIELDS = {
  images: [],
  thumbs: {},
  itemLocations: {},
  brand: "",
  series: "",
  description: "",
  quantity: 1,
  quantityToRepair: 0,
  quantityRepaired: 0,
  quantityDiscarded: 0,
  quantityDonated: 0,
  cost: null,
  retail: null,
  reason: "",
  locked: false,
  archived: false,

  // Status
  salesLeads: {},
  sale: null,
  issue: null,
  issueProjectID: "",
  issueSaleID: "",

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(INVENTORY_ITEM_FIELDS);

export const INVENTORY_ITEM_STATUS_TYPES = {
  toRepair: {
    order: 1,
    label: "To repair",
    icon: "damage",
    color: "negative",
  },
  repaired: {
    order: 2,
    label: "Repaired",
    icon: "done",
    color: "positive",
  },
  available: {
    order: 3,
    label: "Available",
    icon: "available",
    color: "positive",
  },
  pending: {
    order: 4,
    label: "Pending",
    icon: "pending",
    color: "warning",
  },
  donated: {
    order: 5,
    label: "Donated",
    icon: "donated",
    color: "info",
    archive: true,
  },
  discarded: {
    order: 6,
    label: "Discarded",
    icon: "delete",
    color: "negative",
    archive: true,
  },
};
deepFreeze(INVENTORY_ITEM_STATUS_TYPES);

export default class InventoryItem extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, INVENTORY_ITEM_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);
    if (data.salesLeads) {
      Object.keys(data.salesLeads).forEach((leadID) => {
        data.salesLeads[leadID].pendingSince = timestampToDate(
          data.salesLeads[leadID].pendingSince
        );
      });
    }

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      view: InventoryItem.canBeViewedBy,
      create: InventoryItem.canBeCreatedBy,
      create: InventoryItem.canBeCreatedBy,
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
      unlock: this.canBeUnlockedBy,
    });
  }

  // Permissions

  static canBeViewedBy(user) {
    return user.isEmployee;
  }

  static canBeCreatedBy(user) {
    return user.isEmployee;
  }

  canBeEditedBy(user) {
    return (
      this.isEmpty || user.isAdmin || (!this.data.locked && user.isEmployee)
    );
  }

  canBeDeletedBy(user) {
    return (
      !this.data.issue &&
      !this.data.locked &&
      (user.isAdmin ||
        user.hasRole("warehouse") ||
        user.id === this.data.createdBy)
    );
  }

  canBeUnlockedBy(user) {
    return user.isAdmin;
  }

  // Database

  get path() {
    return InventoryItem.getPath(this.id);
  }

  static getPath(id) {
    let path = `inventory`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(inventoryItem) {
    return inventoryItem.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new InventoryItem(snapshot.id, data);
  }

  // Editing

  get rules() {
    return {
      images: [],
      brand: [(a) => Boolean(a.trim()) || "required"],
      series: [],
      description: [(a) => Boolean(a.trim()) || "required"],
      quantity: [(a) => a > 0 || "quantityPositive"],
      quantityToRepair: [
        (a) =>
          !a ||
          a <=
            this.data.quantity -
              this.data.quantityRepaired -
              this.data.quantityDiscarded -
              this.data.quantityDonated -
              this.quantityPending ||
          "quantityToRepairExceeds",
      ],
      quantityRepaired: [
        (a) =>
          !a ||
          a <=
            this.data.quantity -
              this.data.quantityToRepair -
              this.data.quantityDiscarded -
              this.data.quantityDonated -
              this.quantityPending ||
          "quantityRepairedExceeds",
      ],
      quantityDiscarded: [
        (a) =>
          !a ||
          a <=
            this.data.quantity -
              this.data.quantityToRepair -
              this.data.quantityRepaired -
              this.data.quantityDonated -
              this.quantityPending ||
          "quantityDiscardedExceeds",
      ],
      quantityDonated: [
        (a) =>
          !a ||
          a <=
            this.data.quantity -
              this.data.quantityToRepair -
              this.data.quantityRepaired -
              this.data.quantityDiscarded -
              this.quantityPending ||
          "quantityDonatedExceeds",
      ],
      cost: [],
      retail: [],
      reason: [
        (a) =>
          !this.data.quantityDiscarded ||
          Boolean(a.trim()) ||
          "reasonRequiredDiscarding",
      ],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(get(this.data, key)) === true)
    );
  }

  get isEmpty() {
    return Boolean(!this.id && isEmpty(diff(this.data, INVENTORY_ITEM_FIELDS)));
  }

  isEqual(inventoryItem) {
    return Boolean(
      this.id === inventoryItem.id &&
        isEmpty(diff(this.data, inventoryItem.data))
    );
  }

  // Formatting

  getAvatar(size) {
    return getAvatar(this.id, size);
  }

  getThumbnail(imageIndex = 0) {
    const image = this.data.images[imageIndex];
    if (image && image.id) {
      return this.data.thumbs[hashCode(image.id)] || null;
    }
    return null;
  }

  get thumbs() {
    return this.data.images.map((image, i) => this.getThumbnail(i));
  }

  get cost() {
    return this.data.cost === null ? null : formatCurrency(this.data.cost);
  }

  get retail() {
    return this.data.retail === null ? null : formatCurrency(this.data.retail);
  }

  get salesLeads() {
    return Object.keys(this.data.salesLeads).map(
      (id) => store.state.main.salesLeads[id]
    );
  }

  get sale() {
    return this.data.sale ? store.state.main.sales[this.data.sale] : null;
  }

  get issue() {
    return this.data.issue ? store.state.main.issues[this.data.issue] : null;
  }

  get itemLocation() {
    let latest = last(Object.keys(this.data.itemLocations).sort());
    return latest
      ? this.data.itemLocations[latest].map((l) => new ItemLocation(l))
      : [];
  }

  get itemLocations() {
    let locations = [];
    Object.keys(this.data.itemLocations)
      .sort()
      .reverse()
      .forEach((key) => {
        locations.push({
          timestamp: new Date(Number(key)),
          locations: this.data.itemLocations[key].map(
            (l) => new ItemLocation(l)
          ),
        });
      });
    return locations;
  }

  get itemLocationItemType() {
    if (this.isScreens) {
      return "screen";
    } else if (this.isHardware) {
      return "hardware";
    } else if (this.isServiceParts) {
      return "part";
    } else {
      return "unit";
    }
  }

  get isScreens() {
    return this.data.series === "Screens";
  }

  get isHardware() {
    return this.data.series === "Hardware";
  }

  get isServiceParts() {
    return this.data.series === "Service Parts";
  }

  get createdBy() {
    return this.data.createdBy && store.state?.main?.users
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy && store.state?.main?.users
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  get quantityAvailable() {
    return (
      this.quantityRemaining -
      this.quantityPending -
      this.data.quantityToRepair -
      this.data.quantityRepaired
    );
  }

  getQuantityAvailableForLead(salesLeadID) {
    const available = this.quantityAvailable;
    const lead = this.data.salesLeads[salesLeadID];
    return available + (lead ? lead.quantity : 0);
  }

  get quantityRemaining() {
    return (
      this.data.quantity -
      this.data.quantityDiscarded -
      this.data.quantityDonated
    );
  }

  get quantityPending() {
    return Object.values(this.data.salesLeads).reduce(
      (total, lead) => total + lead.quantity,
      0
    );
  }

  get statusType() {
    if (this.quantityToRepair) {
      return "toRepair";
    } else if (this.quantityRepaired) {
      return "repaired";
    } else if (this.quantityAvailable) {
      return "available";
    } else if (this.data.quantityDiscarded) {
      return "discarded";
    } else if (this.data.quantityDonated) {
      return "donated";
    } else {
      return Object.values(this.data.salesLeads)[0].status;
    }
  }

  get status() {
    const type = INVENTORY_ITEM_STATUS_TYPES[this.statusType];
    return type
      ? { ...type, ...COLORS[type.color], type: this.statusType }
      : null;
  }

  get statuses() {
    let statuses = {};
    let type;
    if (this.quantityToRepair) {
      type = INVENTORY_ITEM_STATUS_TYPES.toRepair;
      statuses["toRepair"] = {
        ...type,
        ...COLORS[type.color],
        quantity: this.quantityToRepair,
      };
    }
    if (this.quantityRepaired) {
      type = INVENTORY_ITEM_STATUS_TYPES.repaired;
      statuses["repaired"] = {
        ...type,
        ...COLORS[type.color],
        quantity: this.quantityRepaired,
      };
    }
    if (this.quantityAvailable) {
      type = INVENTORY_ITEM_STATUS_TYPES.available;
      statuses["available"] = {
        ...type,
        ...COLORS[type.color],
        quantity: this.quantityAvailable,
      };
    }
    if (this.data.quantityDiscarded) {
      type = INVENTORY_ITEM_STATUS_TYPES.discarded;
      statuses["discarded"] = {
        ...type,
        ...COLORS[type.color],
        quantity: this.data.quantityDiscarded,
      };
    }
    if (this.data.quantityDonated) {
      type = INVENTORY_ITEM_STATUS_TYPES.donated;
      statuses["donated"] = {
        ...type,
        ...COLORS[type.color],
        quantity: this.data.quantityDonated,
      };
    }
    forEach(this.data.salesLeads, (lead, id) => {
      if ("pending" in statuses) {
        statuses.pending.quantity += lead.quantity;
        statuses.pending.leads.push([id]);
      } else {
        type = INVENTORY_ITEM_STATUS_TYPES.pending;
        statuses.pending = {
          ...type,
          ...COLORS[type.color],
          quantity: lead.quantity,
          leads: [id],
        };
      }
    });
    return sortBy(
      map(statuses, (status, type) => ({ ...status, type })),
      "order"
    );
  }

  get description() {
    return highlightDescription(this.data.description);
  }

  summary(length = 200) {
    const description = this.data.description.replace(
      /^\*\*+[^*]+\*\*+\s*/gm,
      ""
    );
    return length === null
      ? description
      : description.slice(0, length) + (description.length > length ? "…" : "");
  }

  toString() {
    return compact([this.data.brand, this.data.series, this.summary()]).join(
      "\n"
    );
  }
}
